import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRegister } from '../hooks/useRegister';
import { RegisterFormData } from '../types/auth.types';

const registerSchema = z
  .object({
    userId: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters').max(100, 'Password must be less than 100 characters'),
    firstName: z.string().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
    middleName: z.string().max(100, 'Middle name must be less than 100 characters').optional().or(z.literal('')),
    lastName: z.string().min(1, 'Last name is required').max(100, 'Last name must be less than 100 characters'),
    mobile: z.string().max(20, 'Mobile must be less than 20 characters').optional().or(z.literal('')),
    referralCode: z.string().max(100, 'Referral code must be less than 100 characters').optional().or(z.literal('')),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const RegisterForm = () => {
  const navigate = useNavigate();
  const { register: registerUser, loading, error } = useRegister();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    const { confirmPassword: _, ...rest } = data;
    const credentials = {
      ...rest,
      middleName: rest.middleName?.trim() || undefined,
      referralCode: rest.referralCode?.trim() || undefined,
    };
    const success = await registerUser(credentials);
    if (success) {
      // Redirect to verify-email page that shows "check your email" message
      navigate('/verify-email?sent=1', { state: { userId: data.userId } });
    }
  };

  return (
    <Card className="border-border shadow-medium">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-xl">Create Account</CardTitle>
        <CardDescription>Enter your details to create a new account</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="userId">Email</Label>
            <Input
              id="userId"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              {...register('userId')}
              className={errors.userId ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.userId && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.userId.message}
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                autoComplete="given-name"
                {...register('firstName')}
                className={errors.firstName ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="middleName">Middle Name</Label>
              <Input
                id="middleName"
                type="text"
                placeholder=""
                autoComplete="additional-name"
                {...register('middleName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                autoComplete="family-name"
                {...register('lastName')}
                className={errors.lastName ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile (Optional)</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="+1234567890"
              autoComplete="tel"
              {...register('mobile')}
            />
            {errors.mobile && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.mobile.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="referralCode">Referral code (Optional)</Label>
            <Input
              id="referralCode"
              type="text"
              placeholder="e.g. REF123"
              autoComplete="off"
              {...register('referralCode')}
            />
            {errors.referralCode && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.referralCode.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              placeholder="Create a password"
              autoComplete="new-password"
              {...register('password')}
              className={errors.password ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.password && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm your password"
              autoComplete="new-password"
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full h-11"
            loading={loading}
            loadingLabel="Creating account..."
          >
            <span className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Create Account
            </span>
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primary/90 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  );
};
